/**
 * 端点与端口约定。
 *
 * 约束来源：CONTRACT.md 第 60 行 "统一后端28080, console25173,
 * widget25174, dev identity/test-host25175"。
 *
 * 端口在此集中声明，避免各应用各写一份魔法数字；实际运行地址仍由各应用的环境变量覆盖。
 */

/** 客服后端 HTTP API 前缀（CONTRACT.md 第 17 行）。 */
export const SUPPORT_API_PREFIX = '/v1/support';

/** 本地开发默认端口。 */
export const LOCAL_PORTS = {
  api: 28_080,
  console: 25_173,
  realtime: 28_080,
  testHost: 25_175,
  widget: 25_174,
} as const;

export interface EndpointInput {
  /** HTTP API 基地址，可含或不含 /v1/support 前缀。 */
  apiBaseUrl: string;
  /** 实时网关基地址（http/https/ws/wss 均可，会被规范化为 ws/wss）。 */
  wsBaseUrl?: string;
}

export interface ResolvedEndpoints {
  /** 追加了 SUPPORT_API_PREFIX 的 HTTP 基地址。 */
  httpBase: string;
  /** 追加了 SUPPORT_API_PREFIX 的 WebSocket 基地址。 */
  wsBase: string;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/u, '');
}

function withPrefix(base: string, prefix: string): string {
  const trimmed = trimTrailingSlash(base);
  return trimmed.endsWith(prefix) ? trimmed : `${trimmed}${prefix}`;
}

/**
 * 规范化 HTTP / WS 基地址。
 * `/v1/support` 前缀只补一次；wsBaseUrl 缺省时按 http→ws / https→wss 从 apiBaseUrl 推导。
 */
export function resolveEndpoints(input: EndpointInput): ResolvedEndpoints {
  const httpBase = withPrefix(input.apiBaseUrl, SUPPORT_API_PREFIX);
  const wsSource = input.wsBaseUrl?.trim();
  const wsBase = wsSource
    ? withPrefix(toWebSocketScheme(wsSource), SUPPORT_API_PREFIX)
    : withPrefix(toWebSocketScheme(httpBase), SUPPORT_API_PREFIX);
  return { httpBase, wsBase };
}

/** 把 http(s) 地址转换为 ws(s) 地址；已是 ws(s) 时原样返回。 */
export function toWebSocketScheme(url: string): string {
  if (url.startsWith('ws://') || url.startsWith('wss://')) return url;
  if (url.startsWith('https://')) return `wss://${url.slice('https://'.length)}`;
  if (url.startsWith('http://')) return `ws://${url.slice('http://'.length)}`;
  return url;
}

/** 实时连接地址。ticket 作为查询参数，代理/日志必须脱敏（此处只负责拼装）。 */
export function buildRealtimeUrl(wsBase: string, ticket: string): string {
  const separator = wsBase.includes('?') ? '&' : '?';
  return `${wsBase}/ws${separator}ticket=${encodeURIComponent(ticket)}`;
}